<script lang="ts">
  /**
   * The pack list that remembers last time — F14.
   *
   * Its own component rather than another section inside the object page, because
   * it is the one part of Waypoint that has to work standing in a garage with no
   * signal. That constraint shapes everything here: it reads through the offline
   * cache, every tick is applied locally first and sent second, and a tick that
   * could not be sent says so in words rather than silently pretending.
   *
   * The list itself is built on the server from the last three trips of this kind
   * — see `packlist.ts`. The screen's job is to make each row's *provenance*
   * visible, because a checklist you cannot argue with is a checklist you stop
   * trusting: every learned row says where it came from, and anything forgotten
   * last time is marked in amber with the word beside the mark.
   */
  import { Icon, Panel, dateZA } from "@steyncd/steyn-design";
  import { loadCached, putOrQueue, net } from "../lib/offline.svelte";
  import type { PackList, PackRow } from "../lib/trips";

  let { tripId }: { tripId: string } = $props();

  let list = $state<PackList | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  /** Null when the data is live; the timestamp of the copy when it is not. */
  let cachedAt = $state<string | null>(null);
  let saveState = $state<"clean" | "saving" | "saved" | "queued">("clean");
  let newItem = $state("");

  const PATH = $derived(`/trips/${tripId}/packlist`);

  async function load() {
    loading = true;
    const r = await loadCached<PackList>(`/trips/${tripId}/packlist`);
    list = r.data;
    error = r.error;
    cachedAt = r.fresh ? null : r.at;
    loading = false;
  }
  $effect(() => {
    void load();
  });

  /**
   * One debounce for the whole list rather than one request per tick.
   *
   * Someone works down a pack list in bursts of five or six ticks. Sending each
   * one is six round trips over a signal that is by definition bad, and six
   * chances for the order to arrive wrong. The PUT carries whole state, so the
   * last one to arrive is correct regardless of what happened to the others.
   */
  let timer: ReturnType<typeof setTimeout> | null = null;
  function schedule() {
    if (timer) clearTimeout(timer);
    saveState = "saving";
    timer = setTimeout(() => void send(), 600);
  }

  async function send() {
    if (!list) return;
    const body = {
      checked: list.rows.filter((r) => r.packed).map((r) => r.id),
      forgotten: list.rows.filter((r) => r.forgottenThisTime).map((r) => r.id),
      extra: list.rows.filter((r) => r.source === "own").map((r) => ({ id: r.id, label: r.label, group: r.group })),
    };
    try {
      const went = await putOrQueue(PATH, body);
      saveState = went ? "saved" : "queued";
    } catch (e) {
      // A 4xx will not succeed on a retry, so it is a real error rather than a
      // signal problem, and the person needs to know their tick did not stick.
      error = e instanceof Error ? e.message : "Could not save the list.";
      saveState = "clean";
    }
  }

  function toggle(row: PackRow) {
    row.packed = !row.packed;
    schedule();
  }

  function forget(row: PackRow) {
    row.forgottenThisTime = !row.forgottenThisTime;
    schedule();
  }

  /** Add something the list did not know about. It is learned by the next trip of
   *  this kind — that is the whole mechanism, and it costs one field. */
  function add() {
    const label = newItem.trim();
    if (!label || !list) return;
    const id = `own_${label.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40)}`;
    if (list.rows.some((r) => r.id === id)) {
      newItem = "";
      return;
    }
    list.rows.push({
      id,
      label,
      group: "Added this time",
      packed: false,
      source: "own",
      forgottenLastTime: false,
      forgottenThisTime: false,
      seenOn: 0,
      note: null,
    });
    newItem = "";
    schedule();
  }

  const groups = $derived.by(() => {
    const out = new Map<string, PackRow[]>();
    for (const r of list?.rows ?? []) {
      const g = out.get(r.group);
      if (g) g.push(r);
      else out.set(r.group, [r]);
    }
    return [...out.entries()];
  });

  const packed = $derived((list?.rows ?? []).filter((r) => r.packed).length);
  const forgotMarks = $derived((list?.rows ?? []).filter((r) => r.forgottenLastTime).length);
</script>

<section class="card sd-panel">
  <div class="head">
    <span class="kicker"><Icon name="chore" size={13} /> The pack list</span>
    <span class="sub num">{packed} of {list?.rows.length ?? 0} in</span>
  </div>

  {#if cachedAt}
    <!-- Never a cached figure dressed as a live one. The date is the point. -->
    <p class="offline">
      <Icon name="pause" size={12} />
      <span><strong>No signal.</strong> This is the list as it was on {dateZA(cachedAt)}, saved on this phone. Ticking still works.</span>
    </p>
  {/if}

  <Panel {loading} {error} isEmpty={!loading && !error && (list?.rows.length ?? 0) === 0} onretry={load} rows={5}>
    {#snippet empty()}
      {list?.sentence ?? "No list yet. Set the kind of trip on this page and the list builds itself from the last three of that kind."}
    {/snippet}

    <p class="sentence">{list?.sentence}</p>

    {#if forgotMarks > 0}
      <p class="warnline">
        <Icon name="alert" size={13} />
        <span><strong>Forgotten last time:</strong> {forgotMarks} {forgotMarks === 1 ? "thing is" : "things are"} marked below. Those are the ones to check first.</span>
      </p>
    {/if}

    {#each groups as [group, rows] (group)}
      <div class="group">
        <div class="glabel">{group}</div>
        {#each rows as r (r.id)}
          <div class="row" class:row--flag={r.forgottenLastTime}>
            <label class="tick">
              <input type="checkbox" checked={r.packed} onchange={() => toggle(r)} />
              <span class="tick__word">{r.packed ? "in" : "not in"}</span>
            </label>

            <div class="grow">
              <div class="row__t" class:row__t--done={r.packed}>{r.label}</div>
              <div class="marks">
                {#if r.forgottenLastTime}
                  <span class="mark mark--warn"><Icon name="alert" size={11} /> forgotten last time</span>
                {/if}
                {#if r.source === "learned"}
                  <span class="mark"><Icon name="trip" size={11} /> learned — carried on {r.seenOn} of the last three</span>
                {/if}
                {#if r.source === "own"}
                  <span class="mark"><Icon name="plus" size={11} /> added on this trip</span>
                {/if}
                {#if r.note}
                  <span class="mark"><Icon name="pause" size={11} /> {r.note}</span>
                {/if}
                {#if r.forgottenThisTime}
                  <span class="mark mark--warn"><Icon name="alert" size={11} /> marked forgotten</span>
                {/if}
              </div>
            </div>

            <button class="lnk" onclick={() => forget(r)} aria-pressed={r.forgottenThisTime}>
              {r.forgottenThisTime ? "Unmark" : "We forgot it"}
            </button>
          </div>
        {/each}
      </div>
    {/each}

    <div class="add">
      <input
        bind:value={newItem}
        placeholder="Something this list did not know about…"
        onkeydown={(e) => e.key === "Enter" && add()}
        aria-label="Add something to the pack list"
      />
      <button class="btn-primary" onclick={add} disabled={!newItem.trim()}>Add it</button>
    </div>

    <p class="state">
      {#if saveState === "queued" || net.pending > 0}
        <span class="mark mark--warn"><Icon name="alert" size={11} /> held on this phone — it goes up when there is signal</span>
      {:else if saveState === "saving"}
        <span class="mark"><Icon name="clock" size={11} /> saving</span>
      {:else if saveState === "saved"}
        <span class="mark mark--ok"><Icon name="check" size={11} /> saved</span>
      {/if}
    </p>
  </Panel>
</section>

<style>
  .card {
    padding: var(--sp-4) var(--pad-x) var(--sp-5);
    border: 1px solid var(--line);
    border-radius: var(--r-surface);
    background: var(--s1);
  }
  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--sp-3);
    margin-bottom: var(--sp-3);
  }
  .kicker { display: inline-flex; align-items: center; gap: 6px; }
  .sub { font-size: var(--fs-small); color: var(--mut); }
  .sentence { margin: 0 0 var(--sp-3); font-size: var(--fs-body); color: var(--tx2); }

  /* Amber rule + glyph + word. Never the colour on its own. */
  .warnline, .offline {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    margin: 0 0 var(--sp-3);
    padding: var(--sp-2) var(--sp-3);
    border-left: 2px solid var(--warn);
    border-radius: var(--r-control);
    background: var(--fill);
    font-size: var(--fs-small);
    color: var(--tx2);
  }
  .offline { border-left-color: var(--mut); }

  .group { margin-bottom: var(--sp-3); }
  .glabel {
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
    padding: var(--sp-2) 0 var(--sp-1);
  }

  .row {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    /* Density comes from the surface, never from a number typed here: this same
       row has to be right at 34px on an operator surface and 52px on home. */
    min-height: var(--row-h);
    padding: var(--sp-2) var(--sp-3);
    border-bottom: 1px solid var(--line);
  }
  .row--flag { border-left: 2px solid var(--warn); }
  .grow { flex: 1; min-width: 0; }
  .row__t { font-size: var(--fs-body); font-weight: 600; color: var(--tx); }
  .row__t--done { color: var(--mut); }

  .tick { display: flex; flex-direction: column; align-items: center; gap: 2px; }
  .tick input { width: 22px; height: 22px; accent-color: var(--acc); }
  .tick__word { font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }

  .marks { display: flex; flex-wrap: wrap; gap: var(--sp-2); margin-top: 2px; }
  .mark {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }
  .mark--warn { color: var(--warn); }
  .mark--ok { color: var(--ok); }

  .add { display: flex; gap: var(--sp-2); margin-top: var(--sp-3); }
  .add input {
    flex: 1;
    padding: 9px 11px;
    font-size: var(--fs-body);
    color: var(--tx);
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--r-control);
  }
  .state { margin: var(--sp-2) 0 0; min-height: 1.2em; }
  .lnk {
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
    white-space: nowrap;
  }
  .lnk:hover { color: var(--acc); }
  .num { font-variant-numeric: tabular-nums; }

  /* The sheet a person actually carries. Chrome and controls go; the list stays. */
  @media print {
    .add, .lnk, .state, .tick input { display: none; }
    .card { border: none; background: none; padding: 0; }
    .row { break-inside: avoid; }
  }
</style>
